import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Package,
  MapPin,
  Phone,
  User,
  Calendar,
  Truck,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { getStatusBgColor, getStatusTextColor } from "@shared/utils/statusColors";
import { format } from "date-fns";
import { getLivreurPackages } from "../services/packagesService";
import LivreurStatusUpdateDialog from "./LivreurStatusUpdateDialog";

export function LivreurDashboard() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== "all")
      params.append("status", statusFilter);
    params.append("limit", limit.toString());
    params.append("skip", ((page - 1) * limit).toString());
    return params.toString();
  };

  // Fetch livreur packages
  const {
    data: packagesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["livreur-packages", statusFilter, page],
    queryFn: () => getLivreurPackages(buildQueryParams()),
  });

  const packages = packagesData?.packages || [];

  // Filter packages by search term (client-side)
  const filteredPackages = packages.filter(
    (pkg) =>
      pkg.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.customer.phone.includes(searchTerm) ||
      pkg.customer.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pkg.trackingNumber &&
        pkg.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "out_for_delivery":
        return <Truck className="h-4 w-4 text-blue-500" />;
      case "delivery_attempted":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("Livreur Dashboard")}
          </h1>
          <p className="text-muted-foreground">
            {t("Manage your assigned packages and update delivery status")}
          </p>
        </div>
        <Badge variant="outline" className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          {filteredPackages.length} {t("packages")}
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("Filters")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("Search")}</label>
              <Input
                placeholder={t(
                  "Search by customer name, phone, city, or tracking number"
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("Status Filter")}
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t("All statuses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All statuses")}</SelectItem>
                  <SelectItem value="assigned">{t("Assigned")}</SelectItem>
                  <SelectItem value="collected">{t("Collected")}</SelectItem>
                  <SelectItem value="out_for_delivery">
                    {t("Out for Delivery")}
                  </SelectItem>
                  <SelectItem value="delivered">{t("Delivered")}</SelectItem>
                  <SelectItem value="delivery_attempted">
                    {t("Delivery Attempted")}
                  </SelectItem>
                  <SelectItem value="returned_to_warehouse">
                    {t("Returned to Warehouse")}
                  </SelectItem>
                  <SelectItem value="refused_by_customer">
                    {t("Refused by Customer")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Packages List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredPackages.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t("No packages found")}
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm || statusFilter
                    ? t("Try adjusting your filters")
                    : t("You don't have any assigned packages yet")}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredPackages.map((pkg) => (
            <Card key={pkg._id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    {/* Package Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(pkg.status)}
                        <Badge className={`${getStatusBgColor(pkg.status)} ${getStatusTextColor(pkg.status)}`}>
                          {t(pkg.status.replace(/_/g, " "))}
                        </Badge>
                        {pkg.trackingNumber && (
                          <Badge variant="outline">{pkg.trackingNumber}</Badge>
                        )}
                      </div>
                      <LivreurStatusUpdateDialog
                        packageData={pkg}
                        onUpdate={refetch}
                        trigger={
                          <Button size="sm" variant="outline">
                            {t("Update Status")}
                          </Button>
                        }
                      />
                    </div>

                    {/* Customer Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {pkg.customer.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono">
                            {pkg.customer.phone}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {pkg.customer.city}
                            {pkg.customer.secteur && ` - ${pkg.customer.secteur}`}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="font-medium text-muted-foreground">
                            {t("Address")}:
                          </span>
                          <p>{pkg.customer.address}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {t("Created")}:{" "}
                            {format(
                              new Date(pkg.createdAt),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Products */}
                    <div className="space-y-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("Products")}:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {pkg.products.map((product, index) => (
                          <Badge key={index} variant="secondary">
                            {product.name} (x{product.quantity})
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm font-medium text-muted-foreground">
                        {t("Total Amount")}:
                      </span>
                      <span className="font-bold text-lg">
                        {pkg.totalAmount} MAD
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {packages.length >= limit && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            {t("Previous")}
          </Button>
          <span className="flex items-center px-4 py-2 text-sm">
            {t("Page")} {page}
          </span>
          <Button
            variant="outline"
            disabled={packages.length < limit}
            onClick={() => setPage(page + 1)}
          >
            {t("Next")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default LivreurDashboard;
